import React from "react"

import PortalMount from "#Components/PortalMount"
import { APP_SWITCHER_ORGANIZATION_QUERY } from "#Components/shared/AppSwitcher"
import { useGraphqlQuery } from "#React/hooks/useGraphqlQuery"
import AppSignalMark from "#React/ui/AppSignalMark"
import Drawer from "#React/ui/Drawer"
import Icon from "#Root/react/ui/Icon"

const DESKTOP_SIDEBAR_QUERY = "(width >= 64rem)"
type ResponsiveSidebarProps = {
  renderContent: (mobile: boolean) => React.ReactNode
  organizationSlug?: string
  triggerLabel?: string
}

const ResponsiveSidebar = ({
  renderContent,
  organizationSlug,
  triggerLabel = "Open navigation menu",
}: ResponsiveSidebarProps) => {
  const [desktop, setDesktop] = React.useState(
    () => window.matchMedia?.(DESKTOP_SIDEBAR_QUERY).matches ?? true,
  )
  const [open, setOpen] = React.useState(false)
  const triggerRef = React.useRef<HTMLButtonElement>(null)
  const logoHref = document.getElementById("appsignal-logo-link")?.getAttribute("href")
  const { data } = useGraphqlQuery<
    { organization: { name: string } | null },
    { slug: string }
  >(APP_SWITCHER_ORGANIZATION_QUERY, {
    variables: { slug: organizationSlug ?? "" },
    enabled: !desktop && !!organizationSlug,
  })

  React.useEffect(() => {
    const query = window.matchMedia?.(DESKTOP_SIDEBAR_QUERY)
    if (!query) {
      return undefined
    }

    const onChange = (event: MediaQueryListEvent) => {
      setDesktop(event.matches)
      setOpen(false)
    }

    query.addEventListener("change", onChange)
    return () => query.removeEventListener("change", onChange)
  }, [])

  if (desktop) {
    return <>{renderContent(false)}</>
  }

  const close = () => {
    setOpen(false)
    triggerRef.current?.focus()
  }

  const closeOnLink = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return
    }

    const link = event.target instanceof Element ? event.target.closest("a[href]") : null
    if (link && !link.hasAttribute("download") && link.getAttribute("target") !== "_blank") {
      setOpen(false)
    }
  }

  return (
    <>
      <PortalMount containerId="react-mobile-nav-toggle" className="flex items-center">
        <button
          ref={triggerRef}
          type="button"
          className="flex items-center justify-center size-10 rounded text-inherit hover:opacity-80 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-current cursor-pointer -ml-2"
          aria-label={triggerLabel}
          aria-expanded={open}
          onClick={() => setOpen(true)}
        >
          <Icon name="fas fa-bars" size="lg" />
        </button>
      </PortalMount>
      <Drawer open={open} onClose={close} direction="left" size="sm" className="max-xs:w-full">
        <Drawer.Header className="pr-3">
          <Drawer.Title>
            <div className="px-1 flex items-center gap-2 text-content-default">
              <a href={logoHref ?? "/accounts"} aria-label="AppSignal home" className="text-link">
                <AppSignalMark />
              </a>
              {organizationSlug && (
                <a href={`/${organizationSlug}`} className="no-underline">
                  {data?.organization?.name ?? "Organization"}
                </a>
              )}
            </div>
          </Drawer.Title>
        </Drawer.Header>
        <Drawer.Content className="p-0 overflow-hidden">
          <div className="h-full flex flex-col" onClickCapture={closeOnLink}>
            {renderContent(true)}
          </div>
        </Drawer.Content>
      </Drawer>
    </>
  )
}

export default ResponsiveSidebar
